import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { VenueMapSceneProps } from "./VenueMapScene";
import type { createVenueMapWebGL } from "./venueMapWebGL";
import { mapAppearanceSourceDocument } from "./venueMapAppearance";

export type WebGLMapStatus = "loading" | "ready" | "failed";
export type WebGLMapFailure =
  "webgl_unavailable" | "webgl_context_lost" | "webgl_render_failed";

/** Load the GPU renderer when the map becomes visible; report failure once. */
export function VenueMapWebGLScene(
  props: VenueMapSceneProps & {
    onStatus: (status: WebGLMapStatus) => void;
    onFailure?: (reason: WebGLMapFailure) => void;
  },
) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const current = useRef(props);
  current.current = props;
  const draw = useRef<() => void>(() => {});
  const lastRenderer = useRef<
    ReturnType<typeof createVenueMapWebGL> | undefined
  >(undefined);
  const [ready, setReady] = useState(false);
  const sourceDocument = mapAppearanceSourceDocument(props.document);

  useLayoutEffect(() => {
    const element = canvas.current!;
    setReady(false);
    current.current.onStatus("loading");
    let renderer: ReturnType<typeof createVenueMapWebGL> | undefined;
    let disposed = false;
    let failed = false;
    let starting = false;
    let drawQueued = false;
    let visible = typeof IntersectionObserver === "undefined";

    const fail = (reason: WebGLMapFailure) => {
      if (disposed || failed) return;
      failed = true;
      current.current.onStatus("failed");
      current.current.onFailure?.(reason);
    };
    draw.current = () => {
      if (
        disposed ||
        failed ||
        !renderer ||
        !visible ||
        document.hidden ||
        drawQueued
      )
        return;
      // Native scroll rounding can immediately reconcile a camera layout with
      // a second commit. Submit only its final state, in the same paint as the
      // HTML pins. Deferring to another animation frame would put the canvas
      // behind those pins when a host drives the camera from its own frame.
      drawQueued = true;
      queueMicrotask(() => {
        drawQueued = false;
        // A queued microtask cannot be cancelled. An edit, unmount, visibility
        // change or lost context must never draw the old renderer afterwards.
        if (disposed || failed || !renderer || !visible || document.hidden)
          return;
        try {
          const { camera, viewportBounds, route, selectedStandId } =
            current.current;
          renderer.updateMap(current.current.document);
          renderer.updateLogos(current.current.logos);
          renderer.render(camera, viewportBounds, route, selectedStandId);
          setReady(true);
          current.current.onStatus("ready");
        } catch {
          fail("webgl_render_failed");
        }
      });
    };
    const activate = async () => {
      if (disposed || failed || !visible || document.hidden) return;
      if (renderer) {
        draw.current();
        return;
      }
      if (starting) return;
      starting = true;
      try {
        const { createVenueMapWebGL } = await import("./venueMapWebGL");
        if (disposed || failed) return;
        starting = false;
        if (!visible || document.hidden) return;
        renderer = createVenueMapWebGL(
          element,
          current.current.document,
          current.current.logos,
        );
        lastRenderer.current = renderer;
        draw.current();
      } catch {
        fail("webgl_unavailable");
      }
    };
    const onVisibility = () => void activate();
    const onContextLost = (event: Event) => {
      event.preventDefault();
      // Fall back once instead of repeatedly allocating on a constrained phone.
      // A later visit can try a fresh context; there is no automatic retry loop.
      fail("webgl_context_lost");
    };
    element.addEventListener("webglcontextlost", onContextLost);
    document.addEventListener("visibilitychange", onVisibility);
    const observer =
      typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver((entries) => {
            visible = entries.some((entry) => entry.isIntersecting);
            void activate();
          });
    observer?.observe(element);
    void activate();
    return () => {
      disposed = true;
      draw.current = () => {};
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      element.removeEventListener("webglcontextlost", onContextLost);
      renderer?.dispose({ loseContext: false });
    };
  }, [sourceDocument]);

  // A document edit keeps this canvas alive. Release its context only when the
  // component itself leaves; the renderer's disposal is intentionally idempotent.
  useLayoutEffect(() => () => lastRenderer.current?.dispose(), []);

  const { camera, viewportBounds, description, route, selectedStandId } = props;
  useLayoutEffect(
    () => draw.current(),
    [
      props.document,
      props.logos,
      camera.sourceElevation,
      camera.imageWidth,
      camera.imageHeight,
      camera.stageWidth,
      camera.stageHeight,
      camera.offsetX,
      camera.offsetY,
      camera.panX,
      camera.panY,
      camera.rotation,
      camera.pitch,
      camera.perspective,
      viewportBounds.x,
      viewportBounds.y,
      viewportBounds.width,
      viewportBounds.height,
      route?.points,
      selectedStandId,
    ],
  );
  const width = Math.max(1, viewportBounds.width);
  const height = Math.max(1, viewportBounds.height);
  const style = {
    position: "absolute" as const,
    left: viewportBounds.x,
    top: viewportBounds.y,
    width,
    height,
    pointerEvents: "none" as const,
    opacity: ready ? 1 : 0,
  };
  return (
    <canvas
      ref={canvas}
      className="venue-map-scene venue-map-webgl"
      role="img"
      aria-label={description}
      aria-hidden={!ready}
      data-camera={JSON.stringify(camera)}
      data-viewport={JSON.stringify(viewportBounds)}
      data-pitch={camera.pitch}
      data-rotation={camera.rotation}
      data-from-stop={route?.fromStopId}
      data-to-stop={route?.toStopId}
      data-selected-stand={selectedStandId}
      style={style}
    />
  );
}
